import { useMemo, useState } from "react";
import PageTitle from "../components/PageTitle";
import { useNavigate } from "react-router-dom";
import MealTypePicker from "../components/MealTypePicker";
import { saveCustomFavorite } from "../lib/favorites";
import { friendlyError } from "../lib/errors";
import { searchGenericFoods, type GenericFood } from "../lib/generic-foods";
import type { FoodItem, MealType } from "../lib/types";

const blankItem = (): FoodItem => ({
  name: "",
  portion: "",
  calories: 0,
  protein_g: 0,
  carbs_g: 0,
  fat_g: 0
});

export default function CreateFavorite() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [mealType, setMealType] = useState<MealType>("snack");
  const [items, setItems] = useState<FoodItem[]>([blankItem()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update(idx: number, patch: Partial<FoodItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function remove(idx: number) {
    setItems((prev) => prev.filter((_, i) => i !== idx));
  }
  function add() {
    setItems((prev) => [...prev, blankItem()]);
  }

  const filledItems = items.filter((it) => it.name.trim());
  const totals = filledItems.reduce(
    (a, i) => ({
      calories: a.calories + i.calories,
      protein_g: a.protein_g + i.protein_g,
      carbs_g: a.carbs_g + i.carbs_g,
      fat_g: a.fat_g + i.fat_g,
      fiber_g: a.fiber_g + (i.fiber_g ?? 0)
    }),
    { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 }
  );

  async function save() {
    if (!name.trim()) {
      setError("Give your recipe a name.");
      return;
    }
    if (filledItems.length === 0) {
      setError("Add at least one ingredient.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveCustomFavorite({
        name: name.trim(),
        items: filledItems,
        meal_type: mealType
      });
      navigate("/favorites");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-400">
          ← Back
        </button>
        <PageTitle>New favorite</PageTitle>
      </div>

      <p className="text-xs text-slate-500">
        Build a custom recipe by combining ingredients. The totals roll up — save it
        and you can log the whole thing with one tap.
      </p>

      <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
        <label className="block">
          <span className="text-xs text-slate-400 uppercase tracking-wide">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. My protein shake"
            className="mt-1 w-full bg-slate-800 border border-slate-700 rounded p-2 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
          />
        </label>

        <div>
          <span className="text-xs text-slate-400 uppercase tracking-wide">Default category</span>
          <div className="mt-1">
            <MealTypePicker value={mealType} onChange={setMealType} />
          </div>
        </div>
      </section>

      <div className="space-y-3">
        {items.map((item, idx) => (
          <IngredientCard
            key={idx}
            index={idx}
            item={item}
            onChange={(patch) => update(idx, patch)}
            onRemove={items.length > 1 ? () => remove(idx) : undefined}
          />
        ))}
        <button
          type="button"
          onClick={add}
          className="w-full py-3 rounded-lg border border-dashed border-slate-700 text-sm text-slate-400 hover:text-emerald-400 hover:border-emerald-700"
        >
          + Add ingredient
        </button>
      </div>

      <div className="p-3 rounded-lg bg-emerald-950 border border-emerald-800">
        <div className="text-emerald-300 text-xs uppercase tracking-wide">Totals</div>
        <div className="text-2xl font-semibold tabular-nums">{Math.round(totals.calories)} kcal</div>
        <div className="text-sm text-emerald-200">
          P {totals.protein_g.toFixed(0)}g · C {totals.carbs_g.toFixed(0)}g · F {totals.fat_g.toFixed(0)}g
          {totals.fiber_g > 0 && ` · Fiber ${totals.fiber_g.toFixed(0)}g`}
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={() => navigate(-1)}
          className="flex-1 py-3 rounded-lg border border-slate-700 text-slate-300"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 py-3 rounded-lg bg-emerald-500 text-slate-950 font-semibold disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save favorite"}
        </button>
      </div>
    </div>
  );
}

interface IngredientCardProps {
  index: number;
  item: FoodItem;
  onChange: (patch: Partial<FoodItem>) => void;
  onRemove?: () => void;
}

function IngredientCard({ index, item, onChange, onRemove }: IngredientCardProps) {
  const [query, setQuery] = useState("");
  const [showMenu, setShowMenu] = useState(false);
  const matches = useMemo(() => (query.trim().length >= 2 ? searchGenericFoods(query, 5) : []), [query]);

  function applyGeneric(g: GenericFood) {
    onChange({
      name: g.name,
      portion: g.portion,
      calories: g.calories,
      protein_g: g.protein_g,
      carbs_g: g.carbs_g,
      fat_g: g.fat_g,
      fiber_g: g.fiber_g
    });
    setQuery("");
    setShowMenu(false);
  }

  return (
    <div className="p-3 rounded-lg bg-slate-900 border border-slate-800 space-y-3">
      <div className="flex justify-between items-center">
        <span className="text-xs text-slate-400">Ingredient {index + 1}</span>
        {onRemove && (
          <button onClick={onRemove} className="text-xs text-slate-500 hover:text-rose-400">
            Remove
          </button>
        )}
      </div>

      <div className="relative">
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowMenu(true);
          }}
          onFocus={() => setShowMenu(true)}
          placeholder="🔍 Quick-fill from generic foods (e.g. 'banana')"
          className="w-full bg-slate-800 border border-slate-700 rounded p-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
        />
        {showMenu && matches.length > 0 && (
          <ul className="absolute z-20 left-0 right-0 mt-1 rounded-lg bg-slate-800 border border-slate-700 shadow-lg overflow-hidden">
            {matches.map((g) => (
              <li key={g.name}>
                <button
                  type="button"
                  onClick={() => applyGeneric(g)}
                  className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-700 flex items-center gap-2"
                >
                  <span className="text-lg">{g.emoji}</span>
                  <span className="flex-1 truncate">{g.name}</span>
                  <span className="text-xs text-slate-500 tabular-nums">{Math.round(g.calories)} kcal</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="space-y-2">
        <input
          value={item.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Name (e.g. Whey protein, scoop)"
          className="w-full bg-transparent border-b border-slate-700 py-1 text-sm focus:outline-none focus:border-emerald-500"
        />
        <input
          value={item.portion}
          onChange={(e) => onChange({ portion: e.target.value })}
          placeholder="Portion (e.g. 1 scoop, 30g)"
          className="w-full bg-transparent border-b border-slate-800 py-1 text-xs text-slate-400 focus:outline-none focus:border-emerald-500"
        />
        <div className="grid grid-cols-4 gap-2 text-sm pt-1">
          <NumField label="kcal" value={item.calories} onChange={(v) => onChange({ calories: v })} />
          <NumField label="P" value={item.protein_g} onChange={(v) => onChange({ protein_g: v })} />
          <NumField label="C" value={item.carbs_g} onChange={(v) => onChange({ carbs_g: v })} />
          <NumField label="F" value={item.fat_g} onChange={(v) => onChange({ fat_g: v })} />
        </div>
      </div>
    </div>
  );
}

function NumField({
  label,
  value,
  onChange
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="flex flex-col">
      <span className="text-xs text-slate-500">{label}</span>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="bg-transparent border-b border-slate-700 py-1 focus:outline-none focus:border-emerald-500"
      />
    </label>
  );
}
